// src/components/admin/TorreDeControl/ChatView.jsx
import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, Image, Mic, Send } from 'lucide-react';
import { socket } from '../../chat/socket';
import MessageBubble from '../../chat/MessageBubble';
import MediaModal from '../../chat/MediaModal';
import { resolveMediaType } from '../../chat/chatUtils';

function ChatView({ chatId, onBackClick, userId }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sessionOpen, setSessionOpen] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const endRef = useRef(null);

  const normalizedUserId = userId == null ? null : Number(String(userId).trim());
  const isAdmin = normalizedUserId === 2;

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (chatId == null) return;

    const currentId = Number(chatId);
    const room = `chat_${currentId}`;

    // Fetch messages
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${currentId}/messages`)
      .then(r => r.json())
      .then(data => {
        setMessages(data);
        if (Array.isArray(data) && data.length) {
          const candidate = data[0].phone_number ?? data[0].from ?? data[0].sender_phone ?? null;
          if (candidate) setPhoneNumber(candidate);
        }
      })
      .catch(err => console.error('Fetch messages error:', err));

    // Fetch session status
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${currentId}/session`)
      .then(r => r.json())
      .then(data => setSessionOpen(!!data.session_open))
      .catch(err => console.error('Fetch session error:', err));

    // Join socket room
    const doJoin = () => {
      socket.emit('admin_join_chat', room, (ack) => {
        console.log('[socket] join ack:', ack);
      });
    };

    if (socket.connected) {
      doJoin();
    } else {
      const onConnect = () => doJoin();
      socket.once('connect', onConnect);
    }

    // Listen for new messages
    const onNewMessage = (msg) => {
      const incomingId = Number(msg?.chat_id);
      if (incomingId !== currentId) return;
      setMessages(prev => [...prev, msg]);
      setPhoneNumber(prev => prev || msg.phone_number || msg.from || msg.sender_phone || null);
    };

    socket.on('new_message', onNewMessage);

    return () => {
      socket.off('new_message', onNewMessage);
      socket.emit('admin_leave_chat', room);
    };
  }, [chatId]);

  function send() {
    if (!draft.trim()) return;
    
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: draft })
    });
    
    setDraft('');
  }

  function sendTemplate() {
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/send_de_camino`, { 
      method: 'POST' 
    })
      .then(r => r.json())
      .then(console.log)
      .catch(console.error);
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Messages Area */}
      <div 
        className="flex-1 overflow-y-auto p-4"
        style={{
          backgroundImage: 'url("https://storage.googleapis.com/fastmax-images/51046e67-3895-44b2-a2a6-bd1020f4b1f3-OIP.jpeg")',
          backgroundRepeat: 'repeat',
          backgroundColor: '#f0f2f5'
        }}
      >
        {messages.map(m => (
          <MessageBubble 
            key={m.id} 
            message={m} 
            mediaType={resolveMediaType(m)}
          />
        ))}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-slate-200">
        {sessionOpen ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMediaModal(true)}
              className="p-2 bg-slate-100 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              title="Enviar imagen"
            >
              <Image className="w-5 h-5" />
            </button>

            <button
              onClick={() => {}}
              disabled
              className="p-2 bg-slate-100 border border-slate-300 text-slate-400 rounded-lg cursor-not-allowed"
              title="Audio deshabilitado temporalmente"
            >
              <Mic className="w-5 h-5" />
            </button>

            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe un mensaje..."
              className="flex-1 bg-white border border-slate-300 text-slate-900 rounded-lg px-4 py-2 outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20 transition-all font-montserrat"
            />

            <button
              onClick={send}
              className="p-2 bg-[#67A43C] text-white rounded-lg font-semibold hover:bg-[#67A43C]/90 transition-colors"
              title="Enviar"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        ) : isAdmin ? (
          <button
            onClick={sendTemplate}
            className="w-full bg-[#67A43C] text-white rounded-lg py-3 font-semibold hover:bg-[#67A43C]/90 transition-colors font-montserrat"
          >
            Enviar plantilla de "Saludo"
          </button>
        ) : (
          <button
            disabled
            className="w-full bg-slate-200 text-slate-500 border border-slate-300 rounded-lg py-3 font-semibold cursor-not-allowed font-montserrat"
          >
            Chat cerrado
          </button>
        )}
      </div>

      {/* Media Modal */}
      {showMediaModal && (
        <MediaModal
          chatId={chatId}
          onClose={() => setShowMediaModal(false)}
        />
      )}
    </div>
  );
}

export default ChatView;